import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CoachButton, Field, PasswordInput, inputClass } from '../components/primitives';
import { apiErrorPayload, isRateLimited, login } from '../lib/api';
import { safeNextPath } from '../lib/course-access';
import { AuthFrame } from './auth-frame';

export function LoginPage() {
  const navigate = useNavigate();
  // Back to where they were (a course page they opened as a visitor), if
  // anywhere; only ever a path inside the app.
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  // Came from a course page: signing up instead carries that course along.
  const joinId = next?.match(/^\/learn\/c\/([^/?#]+)/)?.[1] ?? null;
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login({ identifier: identifier.trim(), password, totp: totp || undefined });
      navigate(next ?? '/', { replace: true });
    } catch (err) {
      const code = apiErrorPayload(err)?.error ?? '';
      if (code === 'totp_required') {
        setNeedsTotp(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else if (code === 'invalid_totp') {
        setError('That code is not right. Try again.');
      } else if (
        code.endsWith('_not_allowed_in_member_portal') ||
        code.endsWith('_not_allowed_here') ||
        code === 'member_portal_disabled'
      ) {
        setError('This account cannot sign in to Miracles Protocol.');
      } else if (code === 'account_archived') {
        setError('This account is closed. Contact support to reopen it.');
      } else if (isRateLimited(err)) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Invalid email or password.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame
      eyebrow="Welcome back"
      title="Log in"
      subtitle="Use the same account you use for the store."
      footer={
        <p>
          New here?{' '}
          <Link
            to={joinId ? `/signup?join=${joinId}` : '/signup'}
            className="text-ink underline underline-offset-4"
          >
            Create your account
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <Field label="Email or username" htmlFor="identifier">
          <input
            id="identifier"
            className={inputClass}
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            required
          />
        </Field>
        {needsTotp && (
          <Field label="Authenticator code" htmlFor="totp">
            <input
              id="totp"
              inputMode="numeric"
              pattern="\d{6}"
              className={inputClass}
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              required
            />
          </Field>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <CoachButton kind="gold" type="submit" block disabled={busy}>
          {busy ? 'Logging in' : 'Log in'}
        </CoachButton>
      </form>
    </AuthFrame>
  );
}
